import React, { useContext, useMemo, useState } from "react";

import InfoIcon from "@mui/icons-material/Info";
import {
  Box,
  Button,
  Grid,
  IconButton,
  Popover,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import clsx from "clsx";
import { Clipboard, Info } from "react-feather";
import { Link, useLocation } from "react-router-dom";
import mainStyles from "styles";

import { SettingValue } from "@safetyculture/s12-apis-connect-web/s12/accounts/settings/v1/settings_pb";
import { SubscriptionType } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";

import {
  convertTimestampToDate,
  formatDateTimeDisplay,
} from "../../../helpers/time";
import { displaySensorDetails } from "../../../pages/org-sensors";
import OrgActionsMenu from "../dashboard/org-actions-menu";
import OrgDefaultTemplateAccess from "../tabs/components/org-default-template-access";
import OrgInspectionQuotaDisplay from "../tabs/components/org-inspection-quota";
import SuspendLiftSuspensionOrgButton from "../tabs/components/suspend-lift-suspension-org-button";
import ProvisionSupportUserButton from "../tabs/user-actions/provision-support-user-button";
import OrgIndustryVertical from "./org-industry-vertical";
import { OrgScimData } from "api/endpoints/scim";
import { SsoAdminData } from "api/endpoints/sso";
import DisplayHeading from "components/shared/display-heading";
import { NO_DISPLAY_DATA, scvLink } from "constants/index";
import {
  OrgDataContext,
  OrgDetailsContext,
  OrgHasSensorsContext,
} from "context/organisation-context";
import { SnackbarContext } from "context/snackbar-context";
import { toUUID } from "helpers/s12-helpers";

// Constants
const LEGAL_AND_SECURITY_EMAIL_SETTING_KEY =
  "organization.legal_and_security_notification_email";

export interface OrgDetailsProps {
  regionCode: string;
  ssoData?: SsoAdminData;
  scimData?: OrgScimData;
  orgSettings: SettingValue[];
  refreshOrgData: () => void;
}

const OrgDetails = ({
  regionCode,
  ssoData,
  scimData,
  orgSettings,
  refreshOrgData,
}: OrgDetailsProps) => {
  const theme = useTheme();
  const classes = mainStyles();
  const location = useLocation();
  const [ssoDetailsAnchor, setSSODetailsAnchor] = useState<HTMLElement | null>(
    null,
  );
  const [scimDetailsAnchor, setScimDetailsAnchor] =
    useState<HTMLElement | null>(null);
  const { orgData } = useContext(OrgDataContext);
  const { orgDetails, setOrgDetails } = useContext(OrgDetailsContext);
  const { orgHasSensors } = useContext(OrgHasSensorsContext);
  const { copyToClipboard } = useContext(SnackbarContext);
  const subscription = useMemo(() => {
    return orgData.subscription && orgData.subscription[0];
  }, [orgData]);

  const legalAndSecurityEmail = useMemo(() => {
    const setting = orgSettings.find(
      (s) => s.key === LEGAL_AND_SECURITY_EMAIL_SETTING_KEY,
    );
    return setting?.value?.value.case === "stringValue"
      ? setting.value.value.value
      : "";
  }, [orgSettings]);

  const isFreeOrg =
    subscription &&
    (subscription.type === SubscriptionType.FREE ||
      subscription.type === SubscriptionType.FREE_ORG);

  const formatCreatedDate = () => {
    if (orgData.createdAt) {
      return formatDateTimeDisplay(convertTimestampToDate(orgData.createdAt));
    } else {
      return "N/A";
    }
  };
  const tableauHref = `${scvLink}${orgData.id}`;

  const handleDefaultTemplateAccessUpdate = (newValue: string) => {
    setOrgDetails((prevOrgDetails) => {
      const updatedOrgDetails = prevOrgDetails.clone();
      updatedOrgDetails.defaultTemplateAccess = newValue;
      return updatedOrgDetails;
    });
  };

  const EnhancedHeader = () => {
    return (
      <Box style={{ display: "flex", alignItems: "flex-start" }}>
        <Typography
          sx={{ flex: "1 1 100%", fontWeight: "bold" }}
          variant="h6"
          component="div"
          data-testid="org-details-header"
        >
          {orgData.name || NO_DISPLAY_DATA}
        </Typography>
        <OrgActionsMenu regionCode={regionCode} orgSettings={orgSettings} />
      </Box>
    );
  };

  return (
    <>
      <DisplayHeading>
        <EnhancedHeader />
      </DisplayHeading>

      {orgData.id ? (
        <Box
          style={{
            borderColor: theme.palette.divider,
            borderWidth: "1px",
            borderStyle: "solid",
            borderRadius: "5px",
          }}
        >
          <Table size="small">
            <TableBody>
              <TableRow data-testid="org-details-team-structure-row">
                <TableCell
                  className={clsx(
                    classes.tableCell,
                    classes.leftCell,
                    classes.tableLeftColPadding,
                  )}
                  align="right"
                >
                  Team Structure
                </TableCell>
                <TableCell
                  className={clsx(
                    classes.tableCell,
                    classes.rightCell,
                    classes.tableRightColPadding,
                  )}
                  align="left"
                >
                  <Box display="flex" alignItems="center" gap={2}>
                    <Box>
                      <b>Users: </b>
                      <div data-testid="org-details-team-structure-total-users">
                        {(orgData.members && orgData.members.length) ||
                          NO_DISPLAY_DATA}
                      </div>
                    </Box>
                    <ProvisionSupportUserButton regionCode={regionCode} />
                  </Box>
                </TableCell>
              </TableRow>
              <TableRow data-testid="org-details-creation-date-and-dc-row">
                <TableCell
                  className={clsx(
                    classes.tableCell,
                    classes.leftCell,
                    classes.tableLeftColPadding,
                  )}
                  align="right"
                >
                  Creation Date & Data centre
                </TableCell>
                <TableCell
                  className={clsx(
                    classes.tableCell,
                    classes.rightCell,
                    classes.tableRightColPadding,
                  )}
                  align="left"
                >
                  <b>Date Created: </b>
                  {formatCreatedDate()}
                  {" | "}
                  <b>Data Center: </b>
                  {regionCode.toUpperCase()}
                </TableCell>
              </TableRow>
              <TableRow data-testid="org-details-email">
                <TableCell
                  className={clsx(
                    classes.tableCell,
                    classes.leftCell,
                    classes.tableLeftColPadding,
                  )}
                  align="right"
                >
                  Organisation owner email
                </TableCell>
                <TableCell
                  className={clsx(
                    classes.tableCell,
                    classes.rightCell,
                    classes.tableRightColPadding,
                  )}
                  align="left"
                >
                  {orgData.info?.email ? (
                    <Typography
                      variant="body2"
                      align="left"
                      sx={classes.clickable}
                      onClick={() => copyToClipboard(orgData.info!.email)}
                    >
                      {orgData.info.email}{" "}
                      <Clipboard
                        color={theme.palette.text.primary}
                        size={"1rem"}
                        className="position-icon"
                      />
                    </Typography>
                  ) : (
                    NO_DISPLAY_DATA
                  )}
                </TableCell>
              </TableRow>
              <TableRow data-testid="org-details-legal-and-security-email">
                <TableCell
                  className={clsx(
                    classes.tableCell,
                    classes.leftCell,
                    classes.tableLeftColPadding,
                  )}
                  align="right"
                >
                  Legal and security notifications email
                </TableCell>
                <TableCell
                  className={clsx(
                    classes.tableCell,
                    classes.rightCell,
                    classes.tableRightColPadding,
                  )}
                  align="left"
                >
                  {legalAndSecurityEmail ? (
                    <Typography
                      variant="body2"
                      align="left"
                      sx={classes.clickable}
                      onClick={() => copyToClipboard(legalAndSecurityEmail)}
                    >
                      {legalAndSecurityEmail}{" "}
                      <Clipboard
                        color={theme.palette.text.primary}
                        size={"1rem"}
                        className="position-icon"
                      />
                    </Typography>
                  ) : (
                    NO_DISPLAY_DATA
                  )}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  className={clsx(
                    classes.tableCell,
                    classes.leftCell,
                    classes.tableLeftColPadding,
                  )}
                  align="right"
                >
                  ID
                </TableCell>
                <TableCell
                  className={clsx(
                    classes.tableCell,
                    classes.rightCell,
                    classes.tableRightColPadding,
                  )}
                  align="left"
                >
                  <Typography
                    variant="body2"
                    align="left"
                    sx={classes.clickable}
                    onClick={() => copyToClipboard(orgData.id)}
                  >
                    {orgData.id}{" "}
                    <Clipboard
                      color={theme.palette.text.primary}
                      size={"1rem"}
                      className="position-icon"
                    />
                  </Typography>
                  <Typography
                    variant="body2"
                    align="left"
                    className="space-top-x-small"
                    sx={classes.clickable}
                    onClick={() => copyToClipboard(toUUID(orgData.id))}
                  >
                    {toUUID(orgData.id)}{" "}
                    <Clipboard
                      color={theme.palette.text.primary}
                      size={"1rem"}
                      className="position-icon"
                    />
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  className={clsx(
                    classes.tableCell,
                    classes.leftCell,
                    classes.tableLeftColPadding,
                  )}
                  align="right"
                >
                  SSO
                </TableCell>
                <TableCell
                  className={clsx(
                    classes.tableCell,
                    classes.rightCell,
                    classes.tableRightColPadding,
                  )}
                  align="left"
                >
                  {ssoData === undefined ? (
                    "Loading..."
                  ) : ssoData.authConf?.ssoConf?.ssoEnabled ? (
                    <Typography
                      variant="body2"
                      align="left"
                      sx={classes.clickable}
                      onClick={(e) =>
                        setSSODetailsAnchor(e.target as HTMLElement)
                      }
                      style={{ display: "inline-block" }}
                      data-testid={"sso-enabled-display"}
                    >
                      ENABLED{" "}
                      <Info
                        color={theme.palette.text.primary}
                        size={"1rem"}
                        className="position-icon"
                      />
                    </Typography>
                  ) : ssoData.authConf?.ssoConf?.connDetails?.connectionName ? (
                    <Typography
                      variant="body2"
                      align="left"
                      sx={classes.clickable}
                      onClick={(e) =>
                        setSSODetailsAnchor(e.target as HTMLElement)
                      }
                      style={{ display: "inline-block" }}
                      data-testid={"sso-disabled-display"}
                    >
                      DISABLED{" "}
                      <Info
                        color={theme.palette.text.primary}
                        size={"1rem"}
                        className="position-icon"
                      />
                    </Typography>
                  ) : (
                    <Typography
                      variant="body2"
                      align="left"
                      style={{ display: "inline-block" }}
                      data-testid={"sso-not-configured-display"}
                    >
                      Not configured{" "}
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  className={clsx(
                    classes.tableCell,
                    classes.leftCell,
                    classes.tableLeftColPadding,
                  )}
                  align="right"
                >
                  SCIM
                </TableCell>
                <TableCell
                  className={clsx(
                    classes.tableCell,
                    classes.rightCell,
                    classes.tableRightColPadding,
                  )}
                  align="left"
                >
                  {scimData === undefined ? (
                    "Loading..."
                  ) : scimData.loadFailed ? (
                    <Typography
                      variant="body2"
                      align="left"
                      style={{ display: "inline-block" }}
                      data-testid={"scim-unavailable-display"}
                    >
                      Unavailable{" "}
                    </Typography>
                  ) : scimData.status?.hasScim ? (
                    <Typography
                      variant="body2"
                      align="left"
                      sx={classes.clickable}
                      onClick={(e) => setScimDetailsAnchor(e.currentTarget)}
                      style={{ display: "inline-block" }}
                      data-testid={"scim-provisioned-display"}
                    >
                      PROVISIONED{" "}
                      <Info
                        color={theme.palette.text.primary}
                        size={"1rem"}
                        className="position-icon"
                      />
                    </Typography>
                  ) : (
                    <Typography
                      variant="body2"
                      align="left"
                      style={{ display: "inline-block" }}
                      data-testid={"scim-no-activity-display"}
                    >
                      No activity recorded{" "}
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  className={clsx(
                    classes.tableCell,
                    classes.leftCell,
                    classes.tableLeftColPadding,
                  )}
                  align="right"
                >
                  Sensors
                </TableCell>
                <TableCell
                  className={clsx(
                    classes.tableCell,
                    classes.rightCell,
                    classes.tableRightColPadding,
                  )}
                  align="left"
                  data-testid={"org-has-sensors-display"}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                    }}
                  >
                    {
                      <Link
                        to={{
                          pathname: `${location.pathname}/sensors`,
                          state: {
                            orgName: orgData.name,
                          },
                        }}
                      >
                        <Button
                          style={{
                            padding: "0",
                            textTransform: "none",
                          }}
                          color="primary"
                          data-testid="organisation-sensors-btn"
                        >
                          {displaySensorDetails(orgHasSensors)
                            ? "Organisation contains sensors"
                            : "No hardware sensors found"}
                        </Button>
                      </Link>
                    }
                  </Box>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  className={clsx(
                    classes.tableCell,
                    classes.leftCell,
                    classes.tableLeftColPadding,
                  )}
                  align="right"
                >
                  History
                </TableCell>
                <TableCell
                  className={clsx(
                    classes.tableCell,
                    classes.rightCell,
                    classes.tableRightColPadding,
                  )}
                  align="left"
                >
                  <Link
                    to={{
                      pathname: `${location.pathname}/history`,
                      state: {
                        orgName: orgData.name,
                      },
                    }}
                    className="remove-link-style"
                  >
                    <Button
                      style={{
                        padding: "0",
                      }}
                      color="primary"
                      data-testid="organisation-history-btn"
                    >
                      Organisation History
                    </Button>
                  </Link>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  className={clsx(
                    classes.tableCell,
                    classes.leftCell,
                    classes.tableLeftColPadding,
                  )}
                  align="right"
                >
                  Single Customer View
                </TableCell>
                <TableCell
                  className={clsx(
                    classes.tableCell,
                    classes.rightCell,
                    classes.tableRightColPadding,
                  )}
                  align="left"
                >
                  <Button
                    style={{
                      padding: "0",
                    }}
                    href={tableauHref}
                    target="_blank"
                    color="primary"
                    data-testid="org-tableau-scv-btn"
                  >
                    Tableau SCV
                  </Button>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  className={clsx(
                    classes.tableCell,
                    classes.leftCell,
                    classes.tableLeftColPadding,
                  )}
                  align="right"
                >
                  Suspend/Lift Suspension
                </TableCell>
                <TableCell
                  className={clsx(
                    classes.tableCell,
                    classes.rightCell,
                    classes.tableRightColPadding,
                  )}
                  align="left"
                >
                  <SuspendLiftSuspensionOrgButton
                    refreshOrgData={refreshOrgData}
                  />
                </TableCell>
              </TableRow>
              {isFreeOrg ? null : (
                <TableRow>
                  <TableCell
                    className={clsx(
                      classes.tableCell,
                      classes.leftCell,
                      classes.tableLeftColPadding,
                    )}
                    align="right"
                  >
                    Additional org inspection quota
                    <Tooltip title="A user's inspection quota is calculated by the sum of the seat type's default inspection quota limit, additional user inspection quota and additional org inspection quota">
                      <span>
                        <IconButton size="small">
                          <InfoIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell
                    className={clsx(
                      classes.tableCell,
                      classes.rightCell,
                      classes.tableRightColPadding,
                    )}
                    align="left"
                  >
                    <OrgInspectionQuotaDisplay regionCode={regionCode} />
                  </TableCell>
                </TableRow>
              )}
              <TableRow>
                <TableCell
                  className={clsx(
                    classes.tableCell,
                    classes.leftCell,
                    classes.tableLeftColPadding,
                  )}
                  align="right"
                >
                  Default template access
                </TableCell>
                <TableCell
                  className={clsx(
                    classes.tableCell,
                    classes.rightCell,
                    classes.tableRightColPadding,
                  )}
                  align="left"
                >
                  <OrgDefaultTemplateAccess
                    regionCode={regionCode}
                    initialValue={orgDetails.defaultTemplateAccess}
                    onUpdate={handleDefaultTemplateAccessUpdate}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  className={clsx(
                    classes.tableCell,
                    classes.leftCell,
                    classes.tableLeftColPadding,
                  )}
                  align="right"
                >
                  Industry vertical
                </TableCell>
                <TableCell
                  className={clsx(
                    classes.tableCell,
                    classes.rightCell,
                    classes.tableRightColPadding,
                  )}
                  align="left"
                >
                  <OrgIndustryVertical
                    regionCode={regionCode}
                    orgId={orgData.id}
                    orgSettings={orgSettings}
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>
      ) : (
        <>
          {Array(8)
            .fill(0)
            .map((_, index) => {
              return (
                <Grid container spacing={3} key={index}>
                  <Grid item xs={3}>
                    <Skeleton variant="text" height="30px" />
                  </Grid>
                  <Grid item xs={9}>
                    <Skeleton variant="text" height="30px" />
                  </Grid>
                </Grid>
              );
            })}
        </>
      )}
      {ssoData?.authConf?.ssoConf?.ssoEnabled ? (
        <Popover
          open={Boolean(ssoDetailsAnchor)}
          anchorEl={ssoDetailsAnchor}
          onClose={() => setSSODetailsAnchor(null)}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
        >
          <Box m={2}>
            <Typography>
              Allow password login:{" "}
              {(ssoData.authConf.pwdConf?.pwdEnabled ?? false).toString()}
            </Typography>
            <Typography>
              Domains:{" "}
              {(ssoData.authConf.ssoConf?.ssoDomains ?? [])
                .map((domainItem) => domainItem.domain)
                .join(", ")}
            </Typography>
          </Box>
        </Popover>
      ) : null}
      {scimData?.status?.hasScim ? (
        <Popover
          open={Boolean(scimDetailsAnchor)}
          anchorEl={scimDetailsAnchor}
          onClose={() => setScimDetailsAnchor(null)}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
        >
          <Box m={2}>
            <Typography>
              Users provisioned: {scimData.status.userCount.toString()}
            </Typography>
            <Typography>
              Groups provisioned: {scimData.status.groupCount.toString()}
            </Typography>
            <Typography>
              Last activity:{" "}
              {scimData.status.lastModifiedAt
                ? formatDateTimeDisplay(
                    convertTimestampToDate(scimData.status.lastModifiedAt),
                  )
                : NO_DISPLAY_DATA}
            </Typography>
          </Box>
        </Popover>
      ) : null}
    </>
  );
};

export default OrgDetails;
